import { saveAs } from 'file-saver';
import { AlarmService } from './alarm.service';
import { Injectable } from '@angular/core';
import { HelperService } from './helper.service';
import { CameraEvent, LockEvent } from 'app/models/report.model';
import { ReportExportRowsService } from './report-export-rows.service';
import { Angular2Csv } from 'angular2-csv';
import { TranslateService } from '@ngx-translate/core';
import { CareRecipientStatistics, IActiveUsersStats, IAlertStatistics, IEmaacStatistics, StatisticsFilters } from 'app/models/statistics.model';


@Injectable({
  providedIn: 'root'
})
export class FileExportService {

  constructor(
    private helper: HelperService,
    private translate: TranslateService,
    private alarmService: AlarmService,
    private reportExportRows: ReportExportRowsService,
  ) {
    this.alarmService.getAlarmReasonTranslations().subscribe((translations) => {
      this._alarmReasonTranslations = new Map(translations.alarmReason.map((x) => [x.key, x]));
      this._alarmCategoryTranslations = new Map(translations.alarmCategory.map((x) => [x.key, x]));
    });
  }

  private _alarmReasonTranslations: Map<string, { key: string; translation: string; categoryId: number; }>;
  private _alarmCategoryTranslations: Map<string, { key: string; translation: string; categoryId: number; }>;

  /**
   * Export alerts to CSV file
   * @param events Events to export
   * @param filename Name for the exported file
   * @param options Options for the CSV
   */
  exportAlertsToCSV(events: any[], filename: string, options: any): void {
    const csvData = this.reportExportRows.getAlertCsvRows(events);
    new Angular2Csv(csvData, filename, options);
  }

  /**
   * Export report alerts to JSON file
   * @param alerts Alerts to export
   * @param filename Filename for the exported file
   */
  exportAlertsToJson(alerts: any[], filename: string): void {
    const jsonData = this._parseAlertsToJson(alerts);
    this.exportJsonFile(jsonData, filename);
  }

  /**
   * Export camera events to CSV file
   * @param events Camera events to export
   * @param filename Name for the exported file
   * @param options Options for the CSV
   */
  exportCameraEventsToCSV(events: CameraEvent[], filename: string, options: any): void {
    const csvData = this.reportExportRows.getCameraVisitCsvRows(events);
    new Angular2Csv(csvData, filename, options);
  }

  /**
   * Export lock events to CSV file
   * @param events Lock events to export
   * @param filename Name for the exported file
   * @param options Options for the CSV
   */
  exportLockEventsToCSV(events: LockEvent[], filename: string, options: any): void {
    const csvData = this.reportExportRows.getLockEventCsvRows(events);
    new Angular2Csv(csvData, filename, options);
  }

  /**
   * Export alert statistics to JSON file
   * @param statistics alert statistics to export
   * @param filters filters used for the statistics
   */
  exportAlertStatisticsJSON(statistics: IAlertStatistics, filters: StatisticsFilters): void {
    const jsonData = structuredClone(statistics);
    const startDate = this.helper.formatDate(filters.startDate.toString(), "DD.MM.YYYY");
    const endDate = this.helper.formatDate(filters.endDate.toString(), "DD.MM.YYYY");

    jsonData["alertsByDays"]?.forEach((obj) => obj["deviceType"] = this._getDeviceType(obj?.deviceType));
    jsonData["alertsByHours"]?.forEach((obj) => obj["deviceType"] = this._getDeviceType(obj?.deviceType));
    jsonData["deviceAlerts"]?.forEach((obj) => obj["deviceType"] = this._getDeviceType(obj?.deviceType));

    jsonData["alertsByTypes"]?.forEach((obj) => {
      for (const activation of obj.activations) {
        activation["alertType"] = this._getAlertType(obj?.deviceType, activation?.group, activation?.node);

        // Remove these keys from the object, since they don't contain any useful information
        delete activation["group"];
        delete activation["node"];
      }
      obj["deviceType"] = this._getDeviceType(obj.deviceType);
    });

    this.exportJSON(JSON.stringify(jsonData, null, 2), `statistics-alerts-${startDate}_${endDate}`);
  }

  /**
   * Export emaac statistics to JSON file
   * @param emaacStatistics emaac statistics to export
   * @param filters filters used for the statistics
   */
  exportEmaacStatisticsJSON(emaacStatistics: IEmaacStatistics, filters: StatisticsFilters): void {
    const jsonData = structuredClone(emaacStatistics);
    const startDate = this.helper.formatDate(filters.startDate.toString(), "DD.MM.YYYY");
    const endDate = this.helper.formatDate(filters.endDate.toString(), "DD.MM.YYYY");

    jsonData["alertsByDays"]?.forEach((obj) => obj["deviceType"] = this._getDeviceType(obj?.deviceType));
    jsonData["alertsByHours"]?.forEach((obj) => obj["deviceType"] = this._getDeviceType(obj?.deviceType));
    jsonData["responseTimes"].alertResponses?.forEach((obj) => obj["deviceType"] = this._getDeviceType(obj?.deviceType));

    jsonData["alertsByAlarmReasons"]?.forEach((obj) => {
      obj["alarmReasonCategory"] = this._translateAlarmCategory(obj?.alarmReasonCategory);

      for (const alert of obj["alarmReasonAlerts"]) {
        alert["alarmReason"] = this._translateAlarmReason(alert?.alarmReason);
      }
    })

    this.exportJSON(JSON.stringify(jsonData, null, 2), `statistics-emaac-${startDate}_${endDate}`);
  }

  /**
   * Export care recipient statistics to JSON file
   * @param careRecipientStats care recipient statistics to export
   * @param filters filters used for the statistics
   */
  exportCareRecipientStatisticsJSON(careRecipientStats: CareRecipientStatistics[][], filters: StatisticsFilters): void {
    const copy = structuredClone(careRecipientStats).flat();
    const startDate = this.helper.formatDate(filters.startDate.toString(), "DD.MM.YYYY");
    const endDate = this.helper.formatDate(filters.endDate.toString(), "DD.MM.YYYY");
    const jsonData: any[] = [];

    for (const stat of copy) {
      const obj = {};

      stat?.data?.alertCountByAlarmReasons?.forEach((reason) => {
        // Translate alarm reason and remove the key from the object for the JSON readability
        reason["alarmReason"] = this._translateAlarmReason(reason.key);
        delete reason["key"];
      });

      obj["totalAlerts"] = stat?.data?.totalAlerts;
      obj["avgWaitTimeSeconds"] = stat?.data?.avgWaitTimeSeconds;
      obj["avgCareTimeSeconds"] = stat?.data?.avgCareTimeSeconds;
      obj["totalAlarmReasons"] = stat?.data?.totalAlarmReasons;
      obj["alertCountByAlarmReasons"] = stat?.data?.alertCountByAlarmReasons;
      obj["recipientName"] = stat?.card?.name;
      obj["devices"] = stat?.card?.devices;
      obj["customerId"] = stat?.card?.customerId;
      obj["notes"] = stat?.card?.notes;

      jsonData.push(obj);
    }

    this.exportJSON(JSON.stringify(jsonData, null, 2), `statistics-care-recipients-${startDate}_${endDate}`);
  }

   /**
   * Export active users statistics to JSON file
   * @param activeUsersStatistics user activity statistics to export
   * @param filters filters used for the statistics
   */
   exportActiveUsersStatisticsJSON(activeUsersStatistics: IActiveUsersStats, filters: StatisticsFilters): void {
    const jsonData = structuredClone(activeUsersStatistics);
    
    const startDate = this.helper.formatDate(filters.startDate.toString(), 'DD.MM.YYYY');
    const endDate = this.helper.formatDate(filters.endDate.toString(), 'DD.MM.YYYY');
    
    this.exportJSON(JSON.stringify(jsonData, null, 2), `statistics-active-users-${startDate}_${endDate}`);
  }
  
  /**
   * Export JSON data to file
   * @param jsonData Data for the JSON file export
   * @param filename Name for the exported file
   */
  exportJsonFile(jsonData: any, filename: string): void {
    const blob = new Blob([JSON.stringify(jsonData)], { type: "text/plain;charset=utf-8" });
    saveAs(blob, filename + ".json");
  }

  exportJSON(json: string, fileName: string): void {
    const blob = new Blob([json], { type: 'application/json' });
    saveAs(blob, fileName + '.json');
  }

  /**
   * Parse report alerts to JSON format
   * @param alerts Alerts to parse
   * @returns Parsed alerts for the JSON
   */
  private _parseAlertsToJson(alerts: any[]): any[] {
    const forJson = structuredClone(alerts);
    const eventTypeIds = ["ema", "call", "sms", "email", "comment"];

    forJson.forEach((row) => {
      let eventTypeId = "ema";

      for (eventTypeId of eventTypeIds) {
        if (typeof row[eventTypeId + "Events"] !== "undefined") {

          for (let i = 0; i < row[eventTypeId + "Events"].length; i++) {

            if (typeof row[eventTypeId + "Events"][i].eventType !== "undefined") {
              row[eventTypeId + "Events"][i].eventType =
                this.translate.instant(row[eventTypeId + "Events"][i].eventType);

              row[eventTypeId + "Events"][i].time =
                this.helper.formatDate(row[eventTypeId + "Events"][i].time, "l LTS");
            }

            if (eventTypeId == "comment") {
              row[eventTypeId + "Events"][i].CommentTime =
                this.helper.formatDate(row[eventTypeId + "Events"][i].CommentTime, "l LTS");
            }
          }
        }
      }

      if (row.alarmReasons.length > 0) {
        for (const alarmReasons of row.alarmReasons) {
          alarmReasons.alarmReason = this._translateAlarmReason(
            alarmReasons.alarmReason
          );

          alarmReasons.alarmReasonCategory = this._translateAlarmCategory(
            alarmReasons.alarmReasonCategory
          );
        }
      }

      const activationStr = this.helper.getActivationName(
        row.activationGroup,
        row.activationNode,
        row.deviceType,
        null,
        0
      );

      if (typeof activationStr === "string") row["activationName"] = activationStr;

      row["deviceTypeName"] = this.helper.getDeviceType(row.deviceType);
      row["completeTime"] = this.helper.formatDate(row.completeTime, "l LTS");
      row["createTime"] = this.helper.formatDate(row.createTime, "l LTS");
      row["receiveTime"] = this.helper.formatDate(row.receiveTime, "l LTS");
    });

    return forJson;
  }

  /**
   * Translate alarm reason
   * @param reasonKey Alarm reason dictionary key
   * @returns Alarm reason translation
   */
  private _translateAlarmReason(reasonKey: string) {
    const obj = this._alarmReasonTranslations.get(reasonKey);
    return obj ? obj.translation : "";
  }

  /**
   * Translate alarm category
   * @param categoryKey Alarm category dictionary key
   * @returns Alarm category translation
   */
  private _translateAlarmCategory(categoryKey: string) {
    const obj = this._alarmCategoryTranslations.get(categoryKey);
    return obj ? obj.translation : "";
  }


  private _getDeviceType(deviceType: string): string {
    return this.helper.getDeviceType(deviceType);
  }

  private _getAlertType(deviceType: string, group: number, node: number): string {
    return this.helper.getActivationName(group, node, deviceType, "", 0);
  }

    /**
   * Export activity logs to CSV or JSON format
   * @param logs Array of activity log entries
   * @param filename Name of the exported file
   * @param options (only for CSV) CSV export options such as headers, separators etc.
   */
  exportActivityLogsToCSV(logs: any[], filename: string, options: any): void {
    new Angular2Csv(logs, filename, options);
  }

  exportActivityLogsToJson(logs: any[], filename: string): void {
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename + '.json';
    a.click();

    window.URL.revokeObjectURL(url);
  }  
}
